import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

const DEFAULT_PLAN = {
  tier: 'pro',
  isPro: true,
  isFree: false,
  limits: { maxHoldings: Infinity, maxAlerts: Infinity, maxPortfolios: 10 },
  isFounder: false,
  planLoading: false,
  _freemiumEnabled: false,
  _isTesting: false,
};

const PlanContext = createContext(DEFAULT_PLAN);

export const usePlan = () => useContext(PlanContext);

// Configurerable limits for each tier
export const LIMITS = {
  free: {
    maxHoldings: 5,
    maxAlerts: 3,
    maxPortfolios: 1,
  },
  pro: {
    maxHoldings: Infinity,
    maxAlerts: Infinity,
    maxPortfolios: 10,
  }
};

export const PlanProvider = ({ children, overrideUid }) => {
  const { currentUser } = useAuth();
  const effectiveUid = overrideUid || currentUser?.uid;
  
  const [freemiumEnabled, setFreemiumEnabled] = useState(false);
  const [testUids, setTestUids] = useState([]);
  
  const [planTier, setPlanTier] = useState('pro'); // Default to pro if freemium is off
  const [isFounder, setIsFounder] = useState(false);
  const [isStripeCustomer, setIsStripeCustomer] = useState(false);
  const [manualProUntil, setManualProUntil] = useState(null);
  const [planLoading, setPlanLoading] = useState(true);

  // 1. Listen to global configuration for the Feature Flag
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'global_state', 'app_config'), (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        setFreemiumEnabled(data.freemiumEnabled || false);
        setTestUids(data.testUids || []);
      } else {
        setFreemiumEnabled(false);
        setTestUids([]);
      }
    });
    return unsub;
  }, []);

  // 2. Listen to user document for plan details (uses overrideUid when impersonating)
  useEffect(() => {
    if (!effectiveUid) {
      setPlanTier('free');
      setIsFounder(false);
      setPlanLoading(false);
      return;
    }

    setPlanLoading(true);
    const unsub = onSnapshot(doc(db, 'users', effectiveUid), (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        setPlanTier(data.planTier || 'free');
        setIsFounder(data.isFounder === true);
        setIsStripeCustomer(!!data.stripeCustomerId);
        setManualProUntil(data.manualProUntil || null);
      } else {
        setPlanTier('free');
        setIsFounder(false);
        setIsStripeCustomer(false);
        setManualProUntil(null);
      }
      setPlanLoading(false);
    });

    return unsub;
  }, [effectiveUid]);

  // 3. Determine the EXACT effective tier considering the Feature Flag and Manual Override
  const isTesting = effectiveUid && testUids.includes(effectiveUid);
  
  let isManualProActive = false;
  if (manualProUntil) {
     const expiryTime = typeof manualProUntil === 'object' && manualProUntil.seconds 
                        ? manualProUntil.seconds * 1000 
                        : new Date(manualProUntil).getTime();
     if (expiryTime > Date.now()) {
         isManualProActive = true;
     }
  }

  const effectiveTier = isManualProActive ? 'pro' : (freemiumEnabled || isTesting ? planTier : 'pro');

  const isPro = effectiveTier === 'pro';
  const limits = LIMITS[effectiveTier] || LIMITS.free;

  return (
    <PlanContext.Provider value={{
      tier: effectiveTier,
      isPro,
      isFree: !isPro,
      limits,
      isFounder,
      isStripeCustomer,
      planLoading,
      // Expose effective freemium state flag (true if globally enabled OR user is test uid)
      _freemiumEnabled: freemiumEnabled || isTesting,
      _isTesting: isTesting
    }}>
      {children}
    </PlanContext.Provider>
  );
};
